import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ImageOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { InboxEmailBody } from '@hub/contracts'
import {
  buildSrcdoc,
  EMAIL_FRAME_SANDBOX,
  sanitizeEmailHtml,
  type EmailTheme,
} from '@/lib/inbox/email-html'
import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'
import { attachmentUrl } from './attachment-preview'
import { QuoteToggle } from './email-text-body'

/** Shown while the first measurement is pending. */
const INITIAL_HEIGHT_PX = 120
/** A mail that makes itself taller than this scrolls inside its frame instead. */
const MAX_HEIGHT_PX = 20_000
/** Late layout that fires no event (web fonts are off, but tables settle). */
const REMEASURE_MS = 500

/**
 * The height the frame needs, so the page is the only vertical scroller.
 * `scrollHeight` of a document is never below its viewport, so it cannot tell
 * how far a frame may shrink: when nothing overflows, the body says.
 */
function contentHeight(doc: Document): number {
  const root = doc.documentElement
  const scrollbar = (doc.defaultView?.innerHeight ?? root.clientHeight) - root.clientHeight
  if (root.scrollHeight > root.clientHeight) return root.scrollHeight + scrollbar
  const body = doc.body
  if (!body) return root.scrollHeight + scrollbar
  const bottom = Math.ceil(body.getBoundingClientRect().bottom)
  return Math.max(bottom, body.scrollHeight) + scrollbar
}

export interface EmailHtmlBodyProps {
  html: string
  inlineImages: InboxEmailBody['inlineImages']
  projectId: string
  emailId: string
  subject: string | null
  /** The attachments shown as pictures in the mail: they need no chip (D6). */
  onInlineAttachments?: (indexes: number[]) => void
  /** The frame's document, new on every render of it: "Ask HUB" reads selections there. */
  onFrameDocument?: (doc: Document | null) => void
}

/**
 * An email's HTML in a scriptless sandboxed frame (board eeqo); what may reach
 * the frame is decided in `web/lib/inbox/email-html.ts`. Remote pictures stay
 * blocked until "Visa bilder", for this open of this email only.
 */
export function EmailHtmlBody({
  html,
  inlineImages,
  projectId,
  emailId,
  subject,
  onInlineAttachments,
  onFrameDocument,
}: EmailHtmlBodyProps) {
  const { t } = useTranslation('inbox')
  const theme: EmailTheme = useTheme().resolved
  const [remoteImages, setRemoteImages] = useState(false)
  const [quoteToggled, setQuoteToggled] = useState(false)
  const frameRef = useRef<HTMLIFrameElement>(null)

  const urlOf = useCallback(
    (index: number) => attachmentUrl(projectId, emailId, index, true),
    [projectId, emailId]
  )
  const email = useMemo(
    () =>
      sanitizeEmailHtml(
        html,
        { inlineImages, attachmentUrl: urlOf, remoteImages },
        {
          subject,
          labels: {
            forwarded: t('body.forwarded'),
            from: t('body.fwdFrom'),
            date: t('body.fwdDate'),
            to: t('body.fwdTo'),
            subject: t('body.fwdSubject'),
          },
        }
      ),
    [html, inlineImages, urlOf, remoteImages, subject, t]
  )
  // A reply of nothing but history must not open as an empty page.
  const collapsible = email.quote.kind === 'reply' && email.quote.hasBody
  const showQuoted = !collapsible || quoteToggled
  const srcdoc = useMemo(
    () =>
      buildSrcdoc(email.body, {
        theme,
        paper: email.paper,
        remoteImages,
        showQuoted,
        attachmentUrl: urlOf,
      }),
    [email, theme, remoteImages, showQuoted, urlOf]
  )

  const measure = useCallback(() => {
    const frame = frameRef.current
    const doc = frame?.contentDocument
    if (!frame || !doc?.documentElement) return
    // The paper card's border is part of the frame's box, not of its document.
    const height = contentHeight(doc) + frame.offsetHeight - frame.clientHeight
    frame.style.height = `${Math.min(height, MAX_HEIGHT_PX)}px`
    doc.documentElement.style.overflowY = height > MAX_HEIGHT_PX ? 'auto' : 'hidden'
  }, [])

  const onLoad = useCallback(() => {
    measure()
    // Pictures load after the frame's own load; `load` does not bubble, capture sees it.
    const doc = frameRef.current?.contentDocument
    doc?.addEventListener('load', measure, true)
    doc?.addEventListener('error', measure, true)
    onFrameDocument?.(doc ?? null)
  }, [measure, onFrameDocument])

  useLayoutEffect(() => {
    onInlineAttachments?.(email.usedCids)
  }, [email.usedCids, onInlineAttachments])

  useEffect(() => () => onFrameDocument?.(null), [onFrameDocument])

  useLayoutEffect(() => {
    const frame = frameRef.current
    if (!frame || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(frame)
    return () => observer.disconnect()
  }, [measure])

  useEffect(() => {
    const timer = window.setTimeout(measure, REMEASURE_MS)
    return () => window.clearTimeout(timer)
  }, [srcdoc, measure])

  return (
    <div data-slot="inbox-email-html" className="space-y-2">
      {email.blockedImages > 0 && !remoteImages && (
        <div
          data-slot="email-images-blocked"
          className="bg-muted/40 text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-3 py-1.5 text-xs"
        >
          <ImageOff className="h-3.5 w-3.5 shrink-0" />
          <span>{t('body.imagesBlocked', { count: email.blockedImages })}</span>
          <button
            type="button"
            onClick={() => setRemoteImages(true)}
            className="text-foreground min-h-6 font-medium underline underline-offset-2"
          >
            {t('body.loadImages')}
          </button>
        </div>
      )}
      <div className="overflow-hidden">
        <iframe
          ref={frameRef}
          title={t('body.frameTitle')}
          sandbox={EMAIL_FRAME_SANDBOX}
          referrerPolicy="no-referrer"
          srcDoc={srcdoc}
          onLoad={onLoad}
          data-paper={email.paper || undefined}
          style={{ height: INITIAL_HEIGHT_PX }}
          className={cn(
            'block w-full border-0',
            email.paper && 'rounded-md border border-border bg-white'
          )}
        />
      </div>
      {collapsible && <QuoteToggle open={showQuoted} onToggle={() => setQuoteToggled((v) => !v)} />}
    </div>
  )
}
